import { FieldError, SegmentedControl, type SegmentedOption } from '@volee/ui'
import * as React from 'react'
import {
  useController,
  type FieldPath,
  type FieldValues,
  type UseFormReturn,
} from 'react-hook-form'

// `SegmentedControl` renders its own label, so — like `ComboboxField` — it stays bare
// instead of nested inside `FormField`, which would draw a second one.

export interface SegmentedFieldProps<TFields extends FieldValues> {
  readonly form: UseFormReturn<TFields>
  readonly name: FieldPath<TFields>
  readonly label: string
  readonly options: readonly SegmentedOption<string>[]
  readonly error?: string
  readonly pending?: boolean
}

/** `useController`, not `setValue`: the reason is `ComboboxField`'s — an error the server
 *  put on this field would otherwise survive a correct choice and block every next submit. */
export function SegmentedField<TFields extends FieldValues>({
  form,
  name,
  label,
  options,
  error,
  pending = false,
}: SegmentedFieldProps<TFields>) {
  const { field } = useController({ control: form.control, name })
  const fieldId = React.useId()
  const errorId = `${fieldId}-error`

  function choose(value: string) {
    field.onChange(value)
    form.clearErrors(name)
  }

  return (
    <div className="flex flex-col gap-2">
      <SegmentedControl
        label={label}
        options={options}
        value={String(field.value ?? '')}
        onChange={choose}
        disabled={pending}
        aria-describedby={error ? errorId : undefined}
        aria-invalid={error ? true : undefined}
      />
      <FieldError id={errorId}>{error}</FieldError>
    </div>
  )
}
